import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

/** One event on the Saga timeline. Mirrors berserkr.fetch_case_timeline. */
export interface TimelineEvent {
  id: string;
  granularity: "chapter" | "episode" | "message";
  event_type: string;
  occurred_at: string;
  significance: "high" | "normal" | string | null;
  description: string;
  evidence_id: string | null;
  item_kind: "evidence" | "filing" | null;
  item_id: string | null;
  actor_party_id: string | null;
  details: {
    title?: string;
    summary?: string;
    start_date?: string;
    end_date?: string;
    members?: string[];
    [k: string]: unknown;
  };
}

export interface CaseTimeline {
  events: TimelineEvent[];
  span: { start: string | null; end: string | null };
}

/**
 * The Saga — full case chronology at three zoom levels (chapter / episode /
 * message). Each analysis phase repopulates berserkr.events; this just reads.
 */
export function useCaseTimeline(caseId: string | undefined) {
  return useQuery<CaseTimeline, Error>({
    queryKey: ["case-timeline", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      if (!caseId) return { events: [], span: { start: null, end: null } };
      const { data, error } = await berserkr().rpc("fetch_case_timeline", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as CaseTimeline) ?? { events: [], span: { start: null, end: null } };
    },
  });
}
